import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../api/client.js'
import { useSession } from '../api/session.js'
import { ThemeToggle } from '../components/theme.js'
import { LANGUAGES, getLocale, t } from '../i18n/index.js'
import { currentPosition } from '../lib/location.js'
import { WorkerShell, useWorkerTasks } from './shell.js'

interface Stats {
  week: { done: number; total: number }
  month: { done: number; total: number }
}

export function StatsPage() {
  const { data: session } = useSession()
  const { data } = useQuery({ queryKey: ['me-stats'], queryFn: () => api<Stats>('/api/me/stats') })
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState<string | null>(null)
  const { data: tasks } = useWorkerTasks()
  const shareLocation = session?.user.shareLocation ?? false
  const [locationBlocked, setLocationBlocked] = useState(false)

  async function chooseLocation(share: boolean) {
    setSaving('location')
    try {
      await api('/api/me/location', { method: 'PATCH', body: { shareLocation: share } })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['session'] }),
        queryClient.invalidateQueries({ queryKey: ['me-tasks'] }),
      ])
    } finally {
      setSaving(null)
    }
    // Asking the phone right after means the browser's own permission prompt
    // appears now, not later in the middle of a stairwell.
    setLocationBlocked(false)
    if (share) void currentPosition(10_000).then((position) => setLocationBlocked(position === null))
  }

  async function chooseLanguage(code: string) {
    setSaving(code)
    try {
      await api('/api/me/locale', { method: 'PATCH', body: { locale: code } })
      await queryClient.invalidateQueries({ queryKey: ['session'] })
    } finally {
      setSaving(null)
    }
  }

  return (
    <WorkerShell title={t('app.stats')}>
      <p style={{ fontSize: 18, marginTop: 0 }}>{session?.user.name}</p>

      <div className="wk-stats">
        <div className="wk-stat">
          <div className="value">{data ? data.week.done : '–'}</div>
          <div className="label">
            {t('stats.week')}
            <br />
            {data ? t('stats.done_of', { done: data.week.done, total: data.week.total }) : ''}
          </div>
        </div>
        <div className="wk-stat">
          <div className="value">{data ? data.month.done : '–'}</div>
          <div className="label">
            {t('stats.month')}
            <br />
            {data ? t('stats.done_of', { done: data.month.done, total: data.month.total }) : ''}
          </div>
        </div>
      </div>

      <p className="muted" style={{ fontSize: 15, marginTop: 24 }}>
        {t('stats.private')}
      </p>

      <div className="wk-section-title">{t('stats.language')}</div>
      <div className="wk-langs" role="radiogroup" aria-label={t('stats.language')}>
        {LANGUAGES.map((language) => (
          <button
            key={language.code}
            type="button"
            role="radio"
            aria-checked={getLocale() === language.code}
            lang={language.code}
            dir={language.code === 'ar' ? 'rtl' : 'ltr'}
            className={`wk-option ${getLocale() === language.code ? 'on' : ''}`}
            disabled={saving !== null}
            onClick={() => void chooseLanguage(language.code)}
          >
            <span className="dot" aria-hidden="true" />
            {language.name}
          </button>
        ))}
      </div>

      {tasks?.gpsEnabled && (
        <>
          <div className="wk-section-title">{t('stats.location')}</div>
          <div className="wk-consent">{t('stats.location_text')}</div>
          <div className="wk-langs" role="radiogroup" aria-label={t('stats.location')}>
            {[true, false].map((value) => (
              <button
                key={String(value)}
                type="button"
                role="radio"
                aria-checked={shareLocation === value}
                className={`wk-option ${shareLocation === value ? 'on' : ''}`}
                disabled={saving !== null}
                onClick={() => void chooseLocation(value)}
              >
                <span className="dot" aria-hidden="true" />
                {value ? t('stats.location_yes') : t('stats.location_no')}
              </button>
            ))}
          </div>
          {shareLocation && locationBlocked && (
            <div className="notice warn" style={{ fontSize: 15, marginTop: 10 }}>
              {t('stats.location_blocked')}
            </div>
          )}
        </>
      )}

      <div className="wk-section-title">{t('stats.appearance')}</div>
      <ThemeToggle className="wk-btn secondary" labels={{ system: t('theme.system'), light: t('theme.light'), dark: t('theme.dark') }} />
      <p className="muted" style={{ fontSize: 15, marginTop: 10 }}>
        {t('stats.auto_hint')}
      </p>
    </WorkerShell>
  )
}
